import React, { useState } from "react";
import type ContentDashboardGroupDto from "../../types/dtos/content-dashboard/ContentDashboardGroupDto";
import ContentDashboardWidgetCard from "./ContentDashboardWidgetCard";
import { isEmployeeContentWidgetVisible } from "../../helpers/contentDashboardWidgets";

interface ContentDashboardGroupProps {
  group: ContentDashboardGroupDto;
  showDivider: boolean;
}

const ContentDashboardGroup: React.FC<ContentDashboardGroupProps> = ({
  group,
  showDivider,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);

  // Filter out hidden widgets and widgets with no current score
  const visibleWidgets = group.widgets.filter((widget) =>
    isEmployeeContentWidgetVisible(widget)
  );

  // Don't render empty groups
  if (visibleWidgets.length === 0) {
    return null;
  }

  // Determine how many widgets to show
  const maxVisible = group.maxVisibleWidgetCount;
  const hasMaxVisible = maxVisible !== null && maxVisible > 0;
  const hasMoreThanMax = hasMaxVisible && visibleWidgets.length > maxVisible;

  // Once expanded, keep showing all widgets. Button will disappear.
  const widgetsToDisplay =
    isExpanded || !hasMaxVisible
      ? visibleWidgets
      : visibleWidgets.slice(0, maxVisible);

  // A banner spans the row only when it is the only visible widget in the
  // group. Widgets behind Show more still count, so the banner does not
  // jump to full width and then snap back when they appear.
  const bannerSpansGroup =
    visibleWidgets.length === 1 && visibleWidgets[0].type === "IMAGE-BANNER";

  // Dynamic grid columns based on number of widgets displayed
  // This ensures widgets fill available space without empty gaps
  // Maximum 3 widgets per row to maintain readability
  const getGridColumns = () => {
    const count = widgetsToDisplay.length;

    // Base responsive classes
    const base = "grid gap-4";

    if (bannerSpansGroup) {
      return `${base} grid-cols-1`;
    }

    // Mobile: always 1 column
    let cols = "grid-cols-1";

    // Tablet and up: 2 columns
    cols += " md:grid-cols-2";

    // Desktop: dynamic based on count, max 3 columns
    if (count === 1) {
      cols += " lg:grid-cols-2"; // Single widget takes half width (1 of 2 columns)
    } else if (count === 2) {
      cols += " lg:grid-cols-2"; // Two widgets side by side
    } else {
      // 3+ widgets: max 3 columns on all larger screens
      cols += " lg:grid-cols-3";
    }

    return `${base} ${cols}`;
  };

  return (
    <div className="space-y-4 !mt-4">
      {/* Group Header */}
      <div>
        <p className="text-lg font-bold text-gray-600">{group.title}</p>
        {group.description && (
          <p className="text-sm text-gray-600">{group.description}</p>
        )}
      </div>

      {/* Widgets Grid - Dynamic responsive layout */}
      <div className={getGridColumns()}>
        {widgetsToDisplay.map((widget) => (
          <ContentDashboardWidgetCard
            key={widget.id}
            widget={widget}
            fullWidth={bannerSpansGroup}
          />
        ))}
      </div>

      {/* Show More Toggle - disappears once expanded */}
      {hasMoreThanMax && !isExpanded && (
        <div className="relative flex items-center mt-6">
          <div className="flex-grow border-t border-gray-200"></div>
          <button
            onClick={() => setIsExpanded(true)}
            className="flex-shrink-0 mx-4 px-3 py-1.5 text-sm font-medium text-gray-600 bg-gray-50 hover:bg-gray-100 rounded-md transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
          >
            Show more
          </button>
          <div className="flex-grow border-t border-gray-200"></div>
        </div>
      )}

      {showDivider && (!hasMoreThanMax || isExpanded) && (
        <div className="relative flex items-center pt-2">
          <div className="flex-grow border-t border-gray-200"></div>
        </div>
      )}
    </div>
  );
};

export default ContentDashboardGroup;
